import { OverlayRef } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit, ChangeDetectionStrategy, Component, EmbeddedViewRef, OnDestroy, TemplateRef,
  ViewChild, ViewContainerRef, computed, inject, signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERNS } from '@core/constants';
import { passwordValidators } from '@core/helpers/password-validator';
import { HttpApiResponse, HttpApiResponseState } from '@core/http';
import { I18nService, LanguageKeys } from '@core/i18n';
import { AppConfigService } from '@core/services';
import { UserApiService } from '@core/user';
import { BaseMixinComponent } from '@mixins/components/base-component';
import { TranslatableComponentMixin } from '@mixins/components/translatable';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { ShellController } from '@shell/services';
import { stopAndPreventEvent } from '@utils/dom/event';
import { isNullishString } from '@utils/string';
import { MirIcon } from '@widgets/mir-icon';
import { PopupComponent, PopupModule, PopupService } from '@widgets/popup';
import { MirOption, MirSelectComponent, MirSelectEvent } from '@widgets/select';
import { SpinnerIcon } from '@widgets/spinner-icon';
import { ToastMessageService } from '@widgets/toast-message';
import { debounceTime } from 'rxjs/operators';

const DEBOUNCE_TIME = 250;
const ICON = {
  visibility: 'visibility',
  hidden: 'visibility_off'
};
const PASSWORD_VALIDATORS = passwordValidators();

type PasswordField = 'old' | 'new' | 'confirm';
type PasswordCriteria = {
  minLength: boolean;
  uppercase: boolean;
  digit: boolean;
  special: boolean;
};

type ErrorState = {
  [K in PasswordField]: boolean;
} & {
  same: boolean;
  noMatch: boolean;
};

type VisibilityState = {
  [K in PasswordField]: boolean;
};


@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mir-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
    PopupModule,
    MirIcon,
    MirSelectComponent,
    SpinnerIcon,
  ],
  providers: [PopupService, UserApiService, ToastMessageService]
})
export class SettingsComponent extends TranslatableComponentMixin(BaseMixinComponent) implements AfterViewInit, OnDestroy {
  @ViewChild('SettingsPopupTemplate')
  private readonly popupTemplate!: TemplateRef<PopupComponent>;

  /** @Inject */
  readonly #viewContainerRef = inject(ViewContainerRef);
  readonly #i18nService = inject(I18nService);
  readonly #appConfigService = inject(AppConfigService);
  readonly #shellController = inject(ShellController);
  readonly #popupService = inject(PopupService);
  readonly #userService = inject(UserApiService);
  readonly #toastService = inject(ToastMessageService);

  #popupOverlayRef!: OverlayRef;
  #popupViewRef!: EmbeddedViewRef<PopupComponent>;
  readonly #instanceId = crypto.randomUUID().slice(0, 8);

  protected readonly MIN_LENGTH = PASSWORD_MIN_LENGTH;
  protected readonly isSendingPassword = signal(false);

  readonly oldPassword = new FormControl<string>(null, [Validators.required]);
  readonly newPassword = new FormControl<string>(null, PASSWORD_VALIDATORS);
  readonly confirmPassword = new FormControl<string>(null, PASSWORD_VALIDATORS);

  readonly visibilityStates = signal<VisibilityState>({
    old: false,
    new: false,
    confirm: false
  });

  readonly errorStates = signal<ErrorState>({
    old: false,
    new: false,
    confirm: false,
    same: false,
    noMatch: false
  });

  readonly inlineAlertMessage = signal<string | null>(null);
  readonly passwordCriteria = signal<PasswordCriteria>({
    minLength: false,
    uppercase: false,
    digit: false,
    special: false
  });

  readonly languages = signal<MirOption[]>([]);

  readonly passwordStrengthCount = computed(() =>
    Object.values(this.passwordCriteria()).filter(Boolean).length
  );

  readonly canSubmitPassword = computed(() => {
    const errors = this.errorStates();
    const hasErrors = Object.values(errors).some(Boolean);
    const hasAllCriteria = Object.values(this.passwordCriteria()).every(Boolean);
    const controlsAreValid = this.oldPassword.valid && this.newPassword.valid && this.confirmPassword.valid;

    return !hasErrors && hasAllCriteria && controlsAreValid;
  });

  readonly inputIds = {
    old: `settings-old-password-input-${this.#instanceId}`,
    new: `settings-new-password-input-${this.#instanceId}`,
    confirm: `settings-confirm-password-input-${this.#instanceId}`
  };

  constructor() {
    super();
    this.#initializeLanguages();
    this.#setupPasswordValidation();
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.#openPopup();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.#disposePopup();
  }

  handleClose(): void {
    this.#disposePopup();
  }

  handleChangeLanguage(event: MirSelectEvent): void {
    this.#i18nService.setLanguage(event.option.name);
  }

  handleBlurPassword(event: Event, field: PasswordField): void {
    stopAndPreventEvent(event);
    this.#getPasswordControl(field).markAsTouched();
    this.#updateErrorStates();
  }

  handleToggleVisibility(event: Event, field: PasswordField): void {
    event.stopPropagation();
    const current = this.visibilityStates();
    this.visibilityStates.set({ ...current, [field]: !current[field] });
  }

  handleSendNewPassword(): void {
    this.#markAllPasswordsAsTouched();
    this.#updateErrorStates();

    const oldPassword = this.oldPassword.value;
    const newPassword = this.newPassword.value;

    if (isNullishString(oldPassword) || isNullishString(newPassword)) { return; }

    this.inlineAlertMessage.set(null);
    this.isSendingPassword.set(true);

    this.#userService.sendNewPassword({ oldPassword, newPassword })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => this.#onPasswordChangeSuccess(response),
        error: (error) => this.#onPasswordChangeError(error)
      });
  }

  getVisibilityIcon(field: PasswordField): string {
    return this.visibilityStates()[field] ? ICON.visibility : ICON.hidden;
  }

  isCriteriaValid(criterion: keyof PasswordCriteria): boolean {
    return this.passwordCriteria()[criterion];
  }

  isStrengthSegmentFilled(index: number): boolean {
    return index < this.passwordStrengthCount();
  }

  getStrengthSegmentClass(): string {
    const count = this.passwordStrengthCount();
    if (count <= 1) return 'password-strength-segment-warn';
    if (count === 2) return 'password-strength-segment-medium';
    return 'password-strength-segment-strong';
  }

  getSegmentClass(segment: number): string {
    if (!this.isStrengthSegmentFilled(segment)) {
      return 'password-strength-segment-empty';
    }
    return this.getStrengthSegmentClass();
  }

  #initializeLanguages(): void {
    const supportedLanguages = this.#appConfigService.get('supportedLanguages') as string[];
    const languages: MirOption[] = supportedLanguages.map((lang, index) => ({
      id: index,
      name: lang,
      label: this.#getLanguageLabel(lang),
      selected: this.#i18nService.language === lang,
      disabled: false
    }));
    this.languages.set(languages);
  }

  #setupPasswordValidation(): void {
    this.oldPassword.valueChanges
      .pipe(takeUntilDestroyed(), debounceTime(DEBOUNCE_TIME))
      .subscribe(() => this.#updateErrorStates());

    this.newPassword.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.#updatePasswordCriteria(value);
        this.#updateErrorStates();
      });

    this.confirmPassword.valueChanges
      .pipe(takeUntilDestroyed(), debounceTime(DEBOUNCE_TIME))
      .subscribe(() => this.#updateErrorStates());
  }

  #updatePasswordCriteria(password: string | null): void {
    const pwd = password ?? '';
    this.passwordCriteria.set({
      minLength: pwd.length >= this.MIN_LENGTH,
      uppercase: PASSWORD_PATTERNS.uppercase.test(pwd),
      digit: PASSWORD_PATTERNS.digit.test(pwd),
      special: PASSWORD_PATTERNS.special.test(pwd)
    });
  }

  #updateErrorStates(): void {
    const old = this.oldPassword.value;
    const newPwd = this.newPassword.value;
    const confirm = this.confirmPassword.value;

    const newErrors: ErrorState = {
      old: isNullishString(old),
      new: isNullishString(newPwd),
      confirm: isNullishString(confirm),
      same: !isNullishString(newPwd) && !isNullishString(old) && newPwd === old,
      noMatch: !isNullishString(newPwd) && !isNullishString(confirm) && newPwd !== confirm
    };

    this.errorStates.set(newErrors);
    if (this.inlineAlertMessage()) this.inlineAlertMessage.set(null);
  }

  #markAllPasswordsAsTouched(): void {
    this.oldPassword.markAsTouched();
    this.newPassword.markAsTouched();
    this.confirmPassword.markAsTouched();
  }

  #getPasswordControl(field: PasswordField): FormControl {
    const controls = { old: this.oldPassword, new: this.newPassword, confirm: this.confirmPassword };
    return controls[field];
  }

  #getLanguageLabel(code: string): string {
    const labelMap: Record<string, string> = {
      [LanguageKeys.French]: 'Francais',
      [LanguageKeys.English]: 'English'
    };
    const key = labelMap[code] ?? LanguageKeys.English;
    return this.translate(`Common.${key}`);
  }

  #openPopup(): void {
    const { overlayRef, viewRef } = this.#popupService.open<PopupComponent>(
      this.popupTemplate,
      this.#viewContainerRef
    );
    this.#popupOverlayRef = overlayRef;
    this.#popupViewRef = viewRef;

    this.#popupOverlayRef.backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.#disposePopup());
  }

  #disposePopup(): void {
    if (this.#popupOverlayRef) {
      this.#popupOverlayRef.dispose();
      this.#popupViewRef.destroy();
      this.#shellController.closeSettings();
    }
  }

  #onPasswordChangeSuccess(response: HttpApiResponse<any>): void {
    this.isSendingPassword.set(false);

    if (response?.state === HttpApiResponseState.Error) {
      const message = response?.error?.description || this.translate('Http.Error.Default');
      this.#toastService.show(message, 'Warn');
      this.inlineAlertMessage.set(message);
    } else {
      this.#toastService.show(this.translate('EditUser.EditSuccess'), 'Success');
      this.inlineAlertMessage.set(null);
    }
  }

  #onPasswordChangeError(error?: any): void {
    this.isSendingPassword.set(false);
    const message = error?.error?.description || error?.description || this.translate('Http.Error.Default');
    this.#toastService.show(message, 'Warn');
    this.inlineAlertMessage.set(message);
  }
}
